import React from 'react';
import { Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from 'jspdf';

export interface TitrationReading {
  id: string;
  runType: 'rough' | 'accurate';
  aliquotCm3: number;
  indicator: string;
  initialReadingCm3: number;
  finalReadingCm3: number;
  titreCm3: number;
  endpointColour: string;
  concordant: boolean | null;
  remarks: string;
  timestamp: string;
}

export interface ReportContext {
  studentName: string;
  practicalTitle: string;
  family: string;
  method: string;
  equation: string;
  result: string;
  uncertainty: string;
  conclusion: string;
}

interface NotebookProps {
  readings: TitrationReading[];
  onReadingsChange: (readings: TitrationReading[]) => void;
  onAutoRecord: () => Omit<TitrationReading, 'id' | 'timestamp' | 'remarks'> | null;
  onManualAdd: () => void;
  reportContext: ReportContext;
  onSaveReport: (blob: Blob) => Promise<void>;
}

export function createTitrationCsv(readings: TitrationReading[]): string {
  const columns: Array<keyof TitrationReading> = [
    'runType', 'aliquotCm3', 'indicator', 'initialReadingCm3', 'finalReadingCm3',
    'titreCm3', 'endpointColour', 'concordant', 'remarks', 'timestamp',
  ];
  const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  return [
    columns.map(escape).join(','),
    ...readings.map((reading) => columns.map((column) => escape(reading[column])).join(',')),
  ].join('\r\n');
}

export function createTitrationPdf(context: ReportContext, readings: TitrationReading[]): Blob {
  const pdf = new jsPDF();
  let y = 18;
  const line = (text: string, spacing = 7) => {
    const wrapped = pdf.splitTextToSize(text, 180) as string[];
    if (y + wrapped.length * spacing > 278) {
      pdf.addPage();
      y = 18;
    }
    pdf.text(wrapped, 14, y);
    y += wrapped.length * spacing;
  };

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(17);
  line('Volumetric Analysis Laboratory Report', 9);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  line(`Student: ${context.studentName || 'Student'}`);
  line(`Date: ${new Date().toLocaleDateString()}`);
  line(`Practical: ${context.practicalTitle}`);
  line(`Family and method: ${context.family} / ${context.method}`);
  line(`Balanced equation: ${context.equation}`);
  line(`Calculated result: ${context.result || 'Not calculated'}`);
  line(`Uncertainty: ${context.uncertainty || 'Not calculated'}`);
  line('Readings (rough titre is excluded from the accurate mean):', 9);
  readings.forEach((reading, index) => {
    line(`${index + 1}. ${reading.runType}; aliquot ${reading.aliquotCm3.toFixed(2)} cm3; ${reading.initialReadingCm3.toFixed(2)} -> ${reading.finalReadingCm3.toFixed(2)} cm3; titre ${reading.titreCm3.toFixed(2)} cm3; endpoint ${reading.endpointColour}; ${reading.concordant === null ? 'not assessed' : reading.concordant ? 'concordant' : 'outlier'}. ${reading.remarks}`);
  });
  line(`Conclusion: ${context.conclusion || 'No conclusion entered.'}`, 6);
  return pdf.output('blob');
}

const Notebook: React.FC<NotebookProps> = ({ readings, onReadingsChange, onAutoRecord, onManualAdd, reportContext, onSaveReport }) => {
  const [exportError, setExportError] = React.useState('');
  const addManualReading = () => {
    const reading = onAutoRecord();
    if (!reading) return;
    onReadingsChange([...readings, {
      ...reading,
      id: `reading-${Date.now()}`,
      timestamp: new Date().toISOString(),
      remarks: '',
    }]);
  };

  const updateReading = (id: string, patch: Partial<TitrationReading>) => {
    onReadingsChange(readings.map((reading) => {
      if (reading.id !== id) return reading;
      const updated = { ...reading, ...patch };
      updated.titreCm3 = Math.max(0, updated.finalReadingCm3 - updated.initialReadingCm3);
      return updated;
    }));
  };

  const exportCsv = () => {
    const blob = new Blob([createTitrationCsv(readings)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'titration-readings.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    try {
      const blob = createTitrationPdf(reportContext, readings);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'titration-lab-report.pdf';
      anchor.click();
      URL.revokeObjectURL(url);
      await onSaveReport(blob);
      setExportError('');
    } catch (error: any) {
      setExportError(error?.response?.data?.message || error?.message || 'The PDF downloaded, but could not be saved to the account. Start a session and try again.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Laboratory notebook</Typography>
          <Typography variant="body2" color="text.secondary">Record raw readings and observations; rough titres are retained but excluded from the mean.</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addManualReading}>Auto-record run</Button>
          <Button size="small" variant="outlined" onClick={onManualAdd}>Manual row</Button>
        </Stack>
      </Box>
      {exportError && <Typography role="alert" variant="body2" color="error" sx={{ mb: 1 }}>{exportError}</Typography>}
      <TableContainer>
        <Table size="small" aria-label="Titration observations and burette readings">
          <TableHead>
            <TableRow>
              <TableCell>No.</TableCell><TableCell>Run</TableCell><TableCell>Initial</TableCell><TableCell>Final</TableCell><TableCell>Titre (cm³)</TableCell><TableCell>Endpoint</TableCell><TableCell>Concordance</TableCell><TableCell>Remarks</TableCell><TableCell aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {readings.map((reading, index) => (
              <TableRow key={reading.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{reading.runType}</TableCell>
                <TableCell><TextField size="small" type="number" inputProps={{ step: 0.05, 'aria-label': `Initial reading ${index + 1}` }} value={reading.initialReadingCm3} onChange={(event) => updateReading(reading.id, { initialReadingCm3: Number(event.target.value) })} /></TableCell>
                <TableCell><TextField size="small" type="number" inputProps={{ step: 0.05, 'aria-label': `Final reading ${index + 1}` }} value={reading.finalReadingCm3} onChange={(event) => updateReading(reading.id, { finalReadingCm3: Number(event.target.value) })} /></TableCell>
                <TableCell>{reading.titreCm3.toFixed(2)}</TableCell>
                <TableCell>{reading.endpointColour}</TableCell>
                <TableCell>{reading.concordant === null ? 'Pending' : reading.concordant ? 'Yes' : 'Repeat'}</TableCell>
                <TableCell>
                  <TextField size="small" inputProps={{ 'aria-label': `Remarks for titre ${index + 1}` }} value={reading.remarks} onChange={(event) => updateReading(reading.id, { remarks: event.target.value })} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" aria-label={`Delete titre ${index + 1}`} onClick={() => onReadingsChange(readings.filter((item) => item.id !== reading.id))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!readings.length && <TableRow><TableCell colSpan={9} align="center">No titres recorded yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>Export CSV</Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => void exportPdf()}>Export PDF report</Button>
      </Box>
    </Paper>
  );
};

export default Notebook;